import { Controller } from "@hotwired/stimulus";

// Initializes Select2 on the host element. Lightweight wrapper —
// just init + dispatch native change so filter-diff sees it.
export default class extends Controller {
  static values = { placeholder: { type: String, default: "Select…" } };

  connect() {
    if (!window.$ || !window.$.fn || !window.$.fn.select2) return;
    this._$el = window.$(this.element);
    // LRPO-5507 (item 15) — scope the dropdown to the SCROLLING body, not the
    // offcanvas root. Parented to the root, the dropdown is positioned against
    // an element that also contains the sticky footer, so an open dropdown on a
    // control low in the panel (Territory, Qualifier, the demographics selects)
    // painted straight over the "Apply filters" / "Reset all" buttons. Inside
    // the body it clips and scrolls with the content it belongs to. Falls back
    // to the old parents where the body isn't present (other render contexts).
    this._scrollParent = this.element.closest(".filter-drawer__body");
    this._$el.select2({
      placeholder: this.placeholderValue,
      width: "100%",
      dropdownParent: this._scrollParent ||
                      this.element.closest(".offcanvas") ||
                      document.body
    });
    this._makeDropdownScrollSafe();
    this._$el.on("change.s2filter", () => {
      // Re-entrancy guard: jQuery's `.on("change.s2filter")` also catches the
      // NATIVE change we dispatch below, which would re-enter this handler and
      // recurse infinitely (RangeError: Maximum call stack size exceeded).
      // Belt-and-braces guard matches filter_range_controller.js's `_syncing`
      // pattern; try/finally guarantees the flag resets even if a listener throws.
      if (this._reEmitting) return;
      this._reEmitting = true;
      try {
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
      } finally {
        this._reEmitting = false;
      }
    });
  }

  // ─── LRPO-5519: make an open dropdown coexist with a scrolling panel ────────
  //
  // Parenting the dropdown to `.filter-drawer__body` (item 15 above) hands it to
  // the one element in the drawer that SCROLLS (`position: relative; overflow-y:
  // auto`, _filter_drawer.scss). Select2's AttachBody decorator does not expect
  // that, and reacts in two ways that fight the user — both confirmed in Chromium:
  //
  //   1. THE SCROLL FREEZE. On open it binds `scroll.select2.<id>` to every
  //      scrollable ancestor of the control and forces the scroll position back to
  //      where it was when the dropdown opened (select2 4.1.0-rc.0, AttachBody
  //      #_attachPositioningHandler):
  //
  //          $watchers.on(scrollEvent, function () {
  //            var position = Utils.GetData(this, 'select2-scroll-position');
  //            $(this).scrollTop(position.y);
  //          });
  //
  //      So the moment a user clicks into Scanner (or any other drawer select),
  //      every wheel tick renders at the new offset and is immediately yanked back
  //      — the panel "jumps" and scrolling feels unstable. That is the whole of
  //      the reported bug.
  //
  //   2. THE OFF-BY-SCROLLTOP. `_positionDropdown` derives `top` from `.offset()`
  //      deltas (viewport space), but the dropdown is absolutely positioned inside
  //      the SCROLLED body, whose padding box is its containing block — content
  //      space. The two differ by exactly `scrollTop`, so a dropdown opened after
  //      scrolling down to its control renders `scrollTop` px ABOVE it (measured:
  //      268px adrift at scrollTop=300). The freeze in (1) is select2 papering over
  //      this: hold the scroll still and its stale coordinates stay "right".
  //
  // The cure for both is to put the dropdown in the coordinate space it actually
  // lives in. Anchored in CONTENT coordinates it sits under its control at any
  // offset and simply travels with the content as the body scrolls — no freeze, no
  // repositioning, no fight. It stays a child of the body, so item 15 holds: it is
  // still clipped by the body and can never paint over the Apply/Reset footer.
  //
  // Instance-level overrides, applied per select2 instance right after init, so
  // nothing outside this drawer is affected. Every touchpoint is feature-detected:
  // if select2's internals ever move, the controller silently keeps stock behavior
  // rather than throwing.
  _makeDropdownScrollSafe() {
    const parent = this._scrollParent;
    if (!parent) return; // not in the drawer — stock select2 behavior is fine

    const instance = this._$el.data("select2");
    const dropdown = instance && instance.dropdown;
    if (!dropdown) return;
    if (typeof dropdown._positionDropdown !== "function") return;
    if (typeof dropdown._attachPositioningHandler !== "function") return;
    if (typeof dropdown._detachPositioningHandler !== "function") return;

    const $ = window.$;
    const events = (container) => {
      const ns = `.select2.${container.id}`;
      return `scroll${ns} resize${ns} orientationchange${ns}`;
    };

    // (1) Reposition on WINDOW scroll/resize exactly as select2 does, but never
    // freeze the scroll position of the panel the user is scrolling.
    dropdown._attachPositioningHandler = (container) => {
      $(window).on(events(container), () => {
        dropdown._positionDropdown();
        dropdown._resizeDropdown();
      });
    };
    dropdown._detachPositioningHandler = (container) => {
      $(window).off(events(container));
    };

    // (2) Re-express select2's viewport-space top/left in the scroll container's
    // content coordinates. Runs after EVERY select2 reposition (open, results
    // render, select, unselect, window scroll), so no code path can reintroduce
    // the off-by-scrollTop.
    const position = dropdown._positionDropdown.bind(dropdown);
    dropdown._positionDropdown = () => {
      position();
      const el = dropdown.$dropdownContainer && dropdown.$dropdownContainer[0];
      if (!el) return;
      el.style.top = `${(parseFloat(el.style.top) || 0) + parent.scrollTop}px`;
      el.style.left = `${(parseFloat(el.style.left) || 0) + parent.scrollLeft}px`;
    };
  }

  disconnect() {
    if (this._$el && window.$ && window.$.fn.select2) {
      this._$el.off("change.s2filter");
      this._$el.select2("destroy");
    }
  }
}
